/**
 * PreferenceToggles — the canonical settings toggle list, shared by
 * MorePage (compact) and AccessibilityPage (full explanations + preview).
 *
 * Every control writes through useUserSettings().update(): optimistic with
 * rollback, offline queueing and honest SyncStatus. Display preferences
 * (reduced motion, high contrast, text size) take effect immediately via
 * the <html> data-attributes applied in useUserSettings.
 */
import { useContext } from 'react'
import { SegmentedControl, Switch } from '@/components/ui'
import { NavCollapseContext } from '@/components/navigation/nav-collapse-context'
import type { SettingsValues } from './useUserSettings'

export type PreferenceTogglesProps = {
  values: SettingsValues
  update: (patch: Partial<SettingsValues>) => void
  /** 'full' adds the longer explanations used on Toegankelijkheid. */
  variant?: 'compact' | 'full'
}

const TEXT_SIZE_OPTIONS = [
  { value: 'small', label: 'Klein' },
  { value: 'normal', label: 'Normaal' },
  { value: 'large', label: 'Groot' },
]

export function PreferenceToggles({ values, update, variant = 'compact' }: PreferenceTogglesProps) {
  const full = variant === 'full'
  // When rendered inside AppShell, the shared nav-collapse state is updated
  // live (it also persists via upsertSettings + the local cache). Outside an
  // AppShell we fall back to the settings write path.
  const navCollapse = useContext(NavCollapseContext)
  const navCollapsedValue = navCollapse ? navCollapse.collapsed : values.navCollapsed
  const handleNavCollapsed = (navCollapsed: boolean) => {
    if (navCollapse) navCollapse.setCollapsed(navCollapsed)
    else update({ navCollapsed })
  }
  return (
    <div className="settings-stack">
      <Switch
        label="Haptiek (trillen)"
        checked={values.haptics}
        onChange={(haptics) => update({ haptics })}
        description={
          full
            ? 'Licht trillen bij belangrijke acties in de app, zoals bevestigen van een taak.'
            : 'Trillen bij acties in de app'
        }
      />
      <Switch
        label="Geluiden"
        checked={values.sounds}
        onChange={(sounds) => update({ sounds })}
        description={
          full
            ? 'Zachte geluidssignalen, bijvoorbeeld tijdens ademhalingsoefeningen. Standaard uit.'
            : 'Zachte geluidssignalen'
        }
      />
      <Switch
        label="Trillen bij ademhaling"
        checked={values.breathingVibration}
        onChange={(breathingVibration) => update({ breathingVibration })}
        description={
          full
            ? 'Een trilsignaal bij elke fase van een ademhalingsoefening, zodat je het scherm niet hoeft te bekijken.'
            : 'Trilsignaal tijdens ademhalingsoefeningen'
        }
      />
      <Switch
        label="Planner-haptiek"
        checked={values.plannerHaptics}
        onChange={(plannerHaptics) => update({ plannerHaptics })}
        description={
          full
            ? 'Een kort trilsignaal bij het afronden of verschuiven van activiteiten in de planner.'
            : 'Trilsignaal in de planner'
        }
      />
      <Switch
        label="Verminderde beweging"
        checked={values.reducedMotion}
        onChange={(reducedMotion) => update({ reducedMotion })}
        description={
          full
            ? 'Vermindert animaties en overgangen in de hele app. Werkt direct, zonder opnieuw te laden.'
            : 'Minder animaties en overgangen'
        }
      />
      <Switch
        label="Hoog contrast"
        checked={values.highContrast}
        onChange={(highContrast) => update({ highContrast })}
        description={
          full
            ? 'Sterkere kleurcontrasten en duidelijkere randen voor tekst en knoppen. Werkt direct.'
            : 'Sterkere kleurcontrasten'
        }
      />
      <SegmentedControl
        label="Tekstgrootte"
        value={values.textSize}
        onChange={(textSize) =>
          update({ textSize: textSize === 'small' || textSize === 'large' ? textSize : 'normal' })
        }
        options={TEXT_SIZE_OPTIONS}
      />
      <Switch
        label="Menu standaard ingeklapt"
        checked={navCollapsedValue}
        onChange={handleNavCollapsed}
        description={
          full
            ? 'Het onderste menu start ingeklapt. Je opent het met de menuknop; ‘Verbergen’ klappt het weer in.'
            : 'Onderste menu start ingeklapt'
        }
      />
    </div>
  )
}
